import { Controller } from "@hotwired/stimulus"

const SCROLL_STEP = 384

export default class extends Controller {
  static targets = ["title", "container", "leftButton", "rightButton"]

  connect () {
    this.boundRememberScroll = this.rememberScroll.bind(this)
    this.boundReveal = this.reveal.bind(this)
    document.addEventListener("turbo:before-frame-render", this.boundRememberScroll)
    document.addEventListener("turbo:morph", this.boundReveal)
    document.addEventListener("turbo:render", this.boundReveal)
    this.updateButtonStates()
    this.updateTitleFromScroll()
  }

  disconnect () {
    document.removeEventListener("turbo:before-frame-render", this.boundRememberScroll)
    document.removeEventListener("turbo:morph", this.boundReveal)
    document.removeEventListener("turbo:render", this.boundReveal)
  }

  // A morph render keeps the reader's scroll and does not run the markup's
  // script, so the flag the server rendered back in would hide the calendar for
  // good. Nothing needs placing in that case -- only showing.
  reveal () {
    if (this.hasContainerTarget) this.containerTarget.removeAttribute("data-awaiting-position")
  }

  // Rendering the calendar frame rebuilds the grid at the start of the year.
  // This controller sits outside that frame, so it can carry the reader's place
  // across for the fresh markup to restore -- otherwise filtering sends them
  // back to today. Other frames on the page leave the grid alone.
  rememberScroll (event) {
    if (event.target.id !== "calendar" || !this.hasContainerTarget) return

    this.element.dataset.rememberedScroll = this.containerTarget.scrollLeft
  }

  containerTargetConnected(container) {
    this.boundHandleScroll = this.handleScroll.bind(this)
    container.addEventListener('scroll', this.boundHandleScroll)
    this.updateTitleFromScroll()
    this.markHorizontalScroll()
  }

  containerTargetDisconnected(container) {
    if (this.boundHandleScroll) {
      container.removeEventListener('scroll', this.boundHandleScroll)
    }
  }

  handleScroll() {
    this.updateButtonStates()
    this.updateTitleFromScroll()
    this.markHorizontalScroll()
  }

  markHorizontalScroll() {
    if (!this.hasContainerTarget) return

    this.containerTarget.toggleAttribute("data-scrolled-x", this.containerTarget.scrollLeft > 0)
  }

  updateTitleFromScroll() {
    if (!this.hasContainerTarget || !this.hasTitleTarget) return

    const view = this.containerTarget.getBoundingClientRect()
    const start = view.left + this.frozenColumnWidth()
    const onScreen = new Map()

    this.containerTarget.querySelectorAll("[data-term-name]").forEach(column => {
      const box = column.getBoundingClientRect()
      const extent = Math.min(box.right, view.right) - Math.max(box.left, start)
      if (extent <= 0) return

      onScreen.set(column.dataset.termName, (onScreen.get(column.dataset.termName) ?? 0) + extent)
    })

    const [ widest ] = [ ...onScreen ].sort(([ , a ], [ , b ]) => b - a)
    if (widest) this.updateTitles(widest[0])
  }

  // The frozen sequence column covers the start of the row, so the weeks on
  // screen begin at its far side rather than at the container's.
  frozenColumnWidth() {
    return this.containerTarget.querySelector(".calendar-sticky-column")?.getBoundingClientRect().width ?? 0
  }

  scrollLeft () {
    if (!this.hasContainerTarget) return

    this.containerTarget.scrollBy({
      left: -SCROLL_STEP,
      behavior: "smooth"
    })

    setTimeout(() => this.updateButtonStates(), 300)
  }

  scrollRight () {
    if (!this.hasContainerTarget) return

    this.containerTarget.scrollBy({
      left: SCROLL_STEP,
      behavior: "smooth"
    })

    setTimeout(() => this.updateButtonStates(), 300)
  }

  updateTitles (text) {
    this.titleTargets.forEach(title => {
      if (title.textContent !== text) title.textContent = text
    })
  }

  updateButtonStates() {
    if (!this.hasContainerTarget) return

    const container = this.containerTarget
    const scrollLeft = container.scrollLeft
    const maxScrollLeft = container.scrollWidth - container.clientWidth

    if (this.hasLeftButtonTarget) {
      this.setDisabled(this.leftButtonTarget, scrollLeft <= 0)
    }

    if (this.hasRightButtonTarget) {
      this.setDisabled(this.rightButtonTarget, scrollLeft >= maxScrollLeft)
    }
  }

  // ui/button paints its disabled state from aria-disabled, so setting the
  // property alone leaves a button that is inert but still looks clickable.
  setDisabled(button, disabled) {
    button.disabled = disabled
    button.setAttribute('aria-disabled', disabled)
  }
}
